import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Plug, ShieldCheck, RotateCcw } from "lucide-react";
import api, { errMsg } from "../lib/api";

const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";
const STATUS_STYLE = {
  sent: "bg-emerald-100 text-emerald-800", queued: "bg-sky-100 text-sky-800", failed: "bg-red-100 text-[#BB162B]",
  not_recorded: "bg-amber-100 text-amber-800", skipped_test_mode: "bg-slate-200 text-slate-700", discarded: "bg-slate-100 text-slate-500 line-through",
};
const STATUS_LABEL = { sent: "Sent", queued: "Queued", failed: "Failed", not_recorded: "Not recorded — lead closed", skipped_test_mode: "Skipped (test mode)", discarded: "Discarded" };
const dt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" }) : "—");

function ModeCard({ status, reload }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    const next = !status.live;
    if (!window.confirm(next ? "Switch Salesforce to LIVE? Lookups and TD Completion write-backs will hit the real CRM." : "Switch to MOCK? Lookups use seeded demo enquiries and write-backs are logged only.")) return;
    setBusy(true);
    try { await api.put("/config", { scope: "global", salesforce_live: next }); toast.success(next ? "Salesforce is LIVE" : "Salesforce in MOCK mode"); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="sf-mode-card" className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-sm font-bold text-[#0E2240]">Salesforce mode</h3>
          <p className="mt-0.5 text-[11px] text-slate-500">{status.configured ? `Login: ${status.login_url}` : "Credentials not configured (SF_LOGIN_URL / SF_CLIENT_ID / SF_CLIENT_SECRET)"}</p>
          {status.instance_url && <p className="text-[11px] text-slate-500">Instance: {status.instance_url}</p>}
        </div>
        <button data-testid="sf-mode-toggle" disabled={busy} onClick={toggle}
          className={`rounded-full px-4 py-1.5 text-xs font-bold ${status.live ? "bg-[#BB162B] text-white" : "bg-slate-200 text-slate-600"}`}>
          {status.live ? "LIVE" : "MOCK"}
        </button>
      </div>
      <div className="mt-3 grid grid-cols-6 gap-1.5 text-center">
        {Object.entries(status.queue).map(([k, v]) => (
          <div key={k} data-testid={`sf-queue-count-${k}`} className={`rounded-lg px-1 py-1.5 ${STATUS_STYLE[k]}`}>
            <div className="text-base font-bold leading-none">{v}</div>
            <div className="mt-0.5 text-[8px] font-bold uppercase leading-tight">{STATUS_LABEL[k].split(" ")[0]}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestConnection() {
  const [phone, setPhone] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true); setRes(null);
    try { const { data } = await api.post("/salesforce/test-connection", { phone: phone || null }); setRes(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const Row = ({ ok, label, detail }) => (
    <div className={`rounded-lg p-2.5 text-xs ${ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-[#BB162B]"}`}>
      <b>{ok ? "✓" : "✗"} {label}</b>{detail && <div className="mt-0.5 break-all font-mono text-[10px] opacity-80">{detail}</div>}
    </div>
  );
  return (
    <div data-testid="sf-test-card" className="rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="font-heading text-sm font-bold text-[#0E2240]">Test connection</h3>
      <p className="mt-0.5 text-[11px] text-slate-500">Requests a token, then runs one Search Lead (read-only — safe on any number).</p>
      <div className="mt-2 flex gap-2">
        <input data-testid="sf-test-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Sample phone (optional)" className={inputCls} />
        <button data-testid="sf-test-connection-btn" disabled={busy} onClick={run}
          className="mt-1 flex items-center gap-1.5 whitespace-nowrap rounded-xl bg-[#0E2240] px-4 text-xs font-bold text-white disabled:opacity-60">
          <Plug size={13} /> {busy ? "Testing…" : "Test Connection"}
        </button>
      </div>
      {res && (
        <div data-testid="sf-test-result" className="mt-3 space-y-1.5">
          <Row ok={res.token.ok} label={res.token.ok ? `Token OK · ${res.token.ms} ms` : "Token request failed"} detail={res.token.ok ? res.token.instance_url : res.token.error} />
          <Row ok={res.search.ok} label={res.search.ok ? `Search Lead OK (${res.search.search_key}) · ${res.search.ms} ms` : "Search Lead failed"} detail={res.search.response || res.search.error} />
        </div>
      )}
    </div>
  );
}

function AllowedNumbers({ status, reload }) {
  const [text, setText] = useState("");
  useEffect(() => { setText((status.allowed_numbers || []).join("\n")); }, [status.allowed_numbers]);
  const save = async () => {
    const nums = text.split(/[\n,;]+/).map((s) => s.replace(/\D/g, "")).filter(Boolean);
    try { await api.put("/config", { scope: "global", sf_writeback_allowed_numbers: nums }); toast.success(nums.length ? `Write-back restricted to ${nums.length} test number(s)` : "Write-back test mode OFF — universal"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const active = (status.allowed_numbers || []).length > 0;
  return (
    <div data-testid="sf-allowed-card" className={`rounded-2xl border p-4 ${active ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center gap-2">
        <ShieldCheck size={15} className={active ? "text-amber-700" : "text-slate-400"} />
        <h3 className="font-heading text-sm font-bold text-[#0E2240]">Write-back test mode — allowed numbers</h3>
      </div>
      <p className="mt-0.5 text-[11px] text-slate-600">
        {active ? "ACTIVE: TD Completion is sent ONLY for enquiries whose phone is listed below; all others are logged as skipped." : "Empty = write-back goes to every enquiry. Add staff test-lead numbers here during rollout."}
      </p>
      <textarea data-testid="sf-allowed-numbers-input" value={text} onChange={(e) => setText(e.target.value)} rows={3}
        placeholder={"One 10-digit number per line\n9847012345"} className={inputCls + " font-mono"} />
      <button data-testid="sf-allowed-save-btn" onClick={save} className="mt-2 w-full rounded-xl bg-[#0E2240] py-2.5 text-xs font-bold text-white">Save allowed numbers</button>
    </div>
  );
}

function SyncLog({ reload }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const load = () => api.get("/salesforce/sync-log", { params: filter ? { status: filter } : {} }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);
  const retry = async (it) => {
    try { await api.post(`/salesforce/sync-log/${it.id}/retry`); toast.success("Re-queued — sent on the next sync cycle"); load(); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="sf-sync-log" className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-heading text-sm font-bold text-[#0E2240]">TD Completion sync log</h3>
        <div className="flex items-center gap-1.5">
          <select data-testid="sf-log-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px]">
            <option value="">All</option>
            {Object.keys(STATUS_LABEL).map((k) => <option key={k} value={k}>{STATUS_LABEL[k]}</option>)}
          </select>
          <button data-testid="sf-log-refresh" onClick={load} className="rounded-lg border border-slate-200 p-1.5 text-slate-500"><RefreshCw size={13} /></button>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {items.length === 0 && <div className="text-xs text-slate-400">No write-backs yet.</div>}
        {items.map((it) => (
          <div key={it.id} data-testid={`sf-log-item-${it.id}`} className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-xs">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold text-[#0E2240]">{it.customer_name || "—"} <span className="font-normal text-slate-500">· {it.reg_no} · {it.model}</span></div>
                <div className="mt-0.5 text-[10px] text-slate-500">{it.customer_phone || "no phone"} · SF {it.enquiry_id || "—"} · {dt(it.created_at)} · {it.payload?.LeadQuality} · {it.payload?.KilometersRun} km</div>
              </div>
              <span data-testid={`sf-log-status-${it.id}`} className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_STYLE[it.status] || "bg-slate-200"}`}>{STATUS_LABEL[it.status] || it.status}</span>
            </div>
            {(it.sf_message || it.last_error) && (
              <div className="mt-1.5 break-words font-mono text-[10px] text-slate-600">
                {it.sf_message && <div>API: {it.sf_message}</div>}
                {it.last_error && it.status !== "sent" && <div>{it.last_error}{it.attempts ? ` (attempt ${it.attempts}/5${it.next_attempt_at ? `, next ${dt(it.next_attempt_at)}` : ""})` : ""}</div>}
              </div>
            )}
            {!["sent", "queued", "discarded"].includes(it.status) && (
              <button data-testid={`sf-log-retry-${it.id}`} onClick={() => retry(it)}
                className="mt-2 flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-bold text-[#0E2240]"><RotateCcw size={11} /> Retry</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SalesforceAdmin() {
  const [status, setStatus] = useState(null);
  const load = () => api.get("/salesforce/status").then((r) => setStatus(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!status) return <div className="mt-4 text-sm text-slate-400">Loading…</div>;
  return (
    <div className="mt-4 space-y-4">
      <ModeCard status={status} reload={load} />
      <TestConnection />
      <AllowedNumbers status={status} reload={load} />
      <SyncLog reload={load} />
    </div>
  );
}
